import { getConfig } from '@/config/auth.config';
import type { AccountInfo, IPublicClientApplication, RedirectRequest } from '@azure/msal-browser';
import { PublicClientApplication } from '@azure/msal-browser';

let msalInstancePromise: Promise<IPublicClientApplication> | undefined;
let redirectPreHandledPromise: Promise<void> | undefined;
let forcedRefreshTokenPromise: Promise<string> | undefined;
let tokenFailurePromise: Promise<void> | undefined;
let logoutPromise: Promise<void> | undefined;

const isInteractionInProgressError = (error: unknown): boolean => {
    if (typeof error !== 'object' || error === null) {
        return false;
    }

    const withErrorCode = error as { errorCode?: unknown; message?: unknown };
    return (
        withErrorCode.errorCode === 'interaction_in_progress' ||
        (typeof withErrorCode.message === 'string' &&
            withErrorCode.message.includes('interaction_in_progress'))
    );
};

const clearStaleInteractionStatus = (clientId: string): void => {
    const possibleKeys = [`msal.${clientId}.interaction.status`, 'msal.interaction.status'];

    [globalThis.sessionStorage, globalThis.localStorage].forEach(storage => {
        possibleKeys.forEach(key => storage.removeItem(key));
    });
};

const accountMatchesConfiguredTenant = (account: AccountInfo): boolean => {
    const tenantId = getConfig().tenantId;
    return !!tenantId && account.tenantId === tenantId;
};

const ensureActiveAccount = (msal: IPublicClientApplication): AccountInfo | undefined => {
    const activeAccount = msal.getActiveAccount();
    if (activeAccount && accountMatchesConfiguredTenant(activeAccount)) {
        return activeAccount;
    }

    const account = msal.getAllAccounts().find(accountMatchesConfiguredTenant);
    if (account) {
        msal.setActiveAccount(account);
    }

    return account;
};

const acquireTokenSilent = async (forceRefresh = false): Promise<string> => {
    const msal = await getOrCreateMsalInstance();
    const account = ensureActiveAccount(msal);
    const { scopes } = getConfig();

    if (!account) {
        return '';
    }

    try {
        const response = await msal.acquireTokenSilent({
            account,
            scopes,
            forceRefresh,
        });

        return response.accessToken || '';
    } catch {
        return '';
    }
};

export const getOrCreateMsalInstance = async (): Promise<IPublicClientApplication> => {
    msalInstancePromise ??= (async () => {
        const { msalConfig } = getConfig();
        const msal = new PublicClientApplication(msalConfig);
        await msal.initialize();
        return msal;
    })();

    return msalInstancePromise;
};

export const preHandleAuthRedirect = async (): Promise<void> => {
    redirectPreHandledPromise ??= (async () => {
        const msal = await getOrCreateMsalInstance();
        const redirectResult = await msal.handleRedirectPromise();
        if (redirectResult?.account) {
            msal.setActiveAccount(redirectResult.account);
        } else {
            ensureActiveAccount(msal);
        }
    })();

    return redirectPreHandledPromise;
};

export const acquireAccessTokenWithForcedRefresh = async (): Promise<string> => {
    forcedRefreshTokenPromise ??= acquireTokenSilent(true).finally(() => {
        forcedRefreshTokenPromise = undefined;
    });

    return forcedRefreshTokenPromise;
};

export const getAccessToken = async (): Promise<string> => {
    const silentToken = await acquireTokenSilent(false);
    if (silentToken) {
        return silentToken;
    }

    return acquireAccessTokenWithForcedRefresh();
};

export const handleTokenAcquisitionFailure = async (): Promise<void> => {
    tokenFailurePromise ??= (async () => {
        await logoutWithRedirect();
    })().finally(() => {
        tokenFailurePromise = undefined;
    });

    return tokenFailurePromise;
};

export const loginWithRedirect = async (redirectStartPage = '/'): Promise<void> => {
    await preHandleAuthRedirect();

    const msal = await getOrCreateMsalInstance();
    const request: RedirectRequest = {
        redirectStartPage,
        scopes: getConfig().scopes,
    };

    try {
        await msal.loginRedirect(request);
    } catch (error) {
        if (!isInteractionInProgressError(error)) {
            throw error;
        }

        clearStaleInteractionStatus(getConfig().msalConfig.auth.clientId);
        await msal.loginRedirect(request);
    }
};

export const logoutWithRedirect = async (): Promise<void> => {
    logoutPromise ??= (async () => {
        const msal = await getOrCreateMsalInstance();
        const account = (await getActiveAccountInfo()) ?? undefined;
        await msal.logoutRedirect({
            account,
            postLogoutRedirectUri: `${globalThis.location.origin}/auth/redirect/logout`,
        });
    })().finally(() => {
        logoutPromise = undefined;
    });

    return logoutPromise;
};

export const getActiveAccountInfo = async (): Promise<AccountInfo | undefined> => {
    const msal = await getOrCreateMsalInstance();
    return ensureActiveAccount(msal);
};

export const hasAuthenticatedAccount = async (): Promise<boolean> => {
    const account = await getActiveAccountInfo();
    return !!account;
};
